import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';
import { useState } from 'react';
import { format } from 'date-fns';
import { GetTournamentsInfoResp } from '@repo/common-types';

interface CreateTicketModalProps {
  onSubmit: (userId: string, tournamentId: string) => void;
  onClose: () => void;
  tournaments: GetTournamentsInfoResp['data'] | undefined;
  isLoadingTournaments: boolean;
  hasTournamentsError?: boolean;
}

const CreateTicketModal = ({
  onSubmit,
  onClose,
  tournaments,
  isLoadingTournaments,
  hasTournamentsError,
}: CreateTicketModalProps) => {
  const [userId, setUserId] = useState('');
  const [tournamentId, setTournamentId] = useState('');

  const handleSubmit = () => {
    if (!userId.trim() || !tournamentId) {
      return;
    }
    onSubmit(userId.trim(), tournamentId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && userId.trim() && tournamentId) {
      handleSubmit();
    }
  };

  const hasTournaments = tournaments && tournaments.length > 0;

  return (
    <Modal isOpen toggle={onClose} className="modal-lg" centered>
      <ModalHeader toggle={onClose}>Create Tournament Ticket</ModalHeader>
      <ModalBody className="d-flex flex-column gap-3">
        <FormGroup className="mb-0">
          <Label for="userId">User ID</Label>
          <Input
            id="userId"
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter user ID to assign ticket to"
            autoFocus
          />
        </FormGroup>

        <FormGroup className="mb-0">
          <Label for="tournamentId">Tournament</Label>
          {isLoadingTournaments ? (
            <div className="d-flex align-items-center text-muted gap-2">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading tournaments...
            </div>
          ) : hasTournamentsError ? (
            <div className="alert alert-danger mb-0">
              <i className="ri-error-warning-line me-1"></i>
              Failed to load eligible tournaments. Please try again later.
            </div>
          ) : !hasTournaments ? (
            <div className="alert alert-info mb-0">
              <i className="ri-information-line me-1"></i>
              No eligible tournaments available. Tournaments must be in CREATED status with a future start time.
            </div>
          ) : (
            <Input
              type="select"
              id="tournamentId"
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
            >
              <option value="">-- Select Tournament --</option>
              {tournaments?.map((tournament) => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name} - ${tournament.totalBuyin} -{' '}
                  {tournament.startTime ? format(new Date(tournament.startTime), 'MM/dd/yyyy HH:mm') : 'No start time'}
                </option>
              ))}
            </Input>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!userId.trim() || !tournamentId || !hasTournaments}>
          Create Ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateTicketModal;
